<template>
  <layout>
    <template v-slot:header>
      <global-navigation></global-navigation>
    </template>

    <header v-if="hasPage && page.show_banner" class="announcement-banner">
      <img v-if="page.banner_image" :src="page.banner_image.wares" :alt="page.banner_image.alt" class="banner-image" />

      <div v-if="page.bannertext" v-html="page.bannertext" class="banner-text"></div>

      <a
        v-if="page.bannerlink && page.bannerlinktext"
        :href="page.bannerlink"
        target="_blank"
        rel="noreferrer"
        class="banner-link mp-button"
        >{{ page.bannerlinktext }}</a
      >
    </header>

    <main v-if="hasPage">
      <h1 class="wcag-hidden">Home</h1>

      <div v-if="site.button && !this.$store.state.navigation.open" class="sticker">
        <vue-link v-if="site.button.link" :to="site.button.link">
          <img :src="site.button.desktop" draggable="false" class="sticker--desktop" />
          <img :src="site.button.mobile" draggable="false" class="sticker--mobile" />
        </vue-link>
        <div v-else>
          <img :src="site.button.desktop" draggable="false" class="sticker--desktop" />
          <img :src="site.button.mobile" draggable="false" class="sticker--mobile" />
        </div>
      </div>

      <paw :class="{ op0: !$store.state.slideshow.paw }" />

      <flex-header>
        <template v-slot:flex>
          <ScrubSlides :slides="page.slides" />
        </template>
        <title-row
          :left="$store.state.slideshow.category"
          :center="$store.state.slideshow.title"
          :right="$store.state.slideshow.year"
        />
      </flex-header>

      <section class="productions" id="productions" ref="productions">
        <hr v-if="hasUpcomingProductions" />
        <gallery class="upcoming" v-if="hasUpcomingProductions">
          <h2>Coming Soon</h2>
          <vue-link
            v-for="entry in page.upcoming"
            :to="entry.active ? entry.url : false"
            :key="entry.url"
            :class="{
              'no-image': !entry.gridimage,
              'has-trailer': entry.active,
            }"
          >
            <figure class="cover noisy-image">
              <img v-if="entry.gridimage" :src="entry.gridimage.small" alt="" />
            </figure>
            <div>
              {{ entry.multilinetitle ? entry.multilinetitle : entry.title }}
              {{ entry.year }}
            </div>
          </vue-link>
        </gallery>

        <hr v-if="hasReleasedProductions" />
        <div class="released" v-if="hasReleasedProductions">
          <h2 class="wcag-hidden">Productions</h2>

          <vue-link v-for="entry in page.released" :to="entry.url" :key="entry.title">
            <figure class="cover">
              <img v-if="entry.gridimage" :src="entry.gridimage.large" alt="" />
            </figure>
            <title-row :left="entry.category" :center="entry.title" :right="entry.year" />
          </vue-link>
        </div>
      </section>
    </main>

    <template v-slot:footer>
      <global-footer></global-footer>
    </template>
  </layout>
</template>

<script>
import PageMixin from '@/mixins/PageMixin'

import ScrubSlides from '@/components/ScrubSlides'
import Gallery from '@/components/Gallery'
import TitleRow from '@/components/TitleRow'
import FlexHeader from '@/components/FlexHeader'
import Paw from '@/components/Paw'

export default {
  name: 'Home',
  mixins: [PageMixin],
  components: { ScrubSlides, Gallery, TitleRow, FlexHeader, Paw },
  computed: {
    hasUpcomingProductions() {
      return !!(this.page.upcoming && this.page.upcoming.length)
    },
    hasReleasedProductions() {
      return !!(this.page.released && this.page.released.length)
    },
  },
  watch: {
    hasPage: 'scrollToProductions',
  },
  methods: {
    scrollToProductions() {
      if (this.$route.hash === '#productions' && this.$refs.productions) {
        this.$scrollTo(this.$refs.productions, 1, {
          offset: -50,
        })
      }
    },
  },
}
</script>

<style scoped>
/**
 * Productions
 */

hr {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.announcement-banner {
  background-color: #00ffff;
  padding: 4rem 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0 2rem 2rem 2rem;
  border-radius: var(--br);
}

.announcement-banner img {
  width: 100%;
  max-width: 500px;
  height: auto;
}

.announcement-banner .banner-text {
  font: var(--nord);
  color: black;
  text-align: center;
  max-width: 800px;
}

.announcement-banner .banner-link {
  color: black;
}

.announcement-banner * + * {
  margin-top: 3rem;
}

main {
  position: relative;
}

.upcoming {
  padding: 2rem 0;
}

.upcoming h2 {
  position: relative;
  width: 4rem;
  height: 20rem;
  vertical-align: top;
  background-image: url(~@/assets/coming-soon.svg);
  background-size: 1.25rem;
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  overflow: hidden;
  text-indent: -9999px;
  white-space: nowrap;
}

@media (max-width: 400px) {
  .upcoming h2 {
    background-size: 1.15rem;
  }
}

.upcoming a {
  margin-left: 2rem;
  position: relative;
  border: 1px dashed transparent;
  border-radius: var(--br);
}

.upcoming a:last-child {
  margin-right: 2rem;
}

.upcoming figure {
  width: 30rem;
  height: 20rem;
  border: none;
  opacity: 0.65;
}

.upcoming figure + div {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 2rem;
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: center;
  font: var(--nord);
  text-transform: uppercase;
  white-space: pre-line;
}

/* upcoming hover handling */
.upcoming a.no-image {
  border-color: #fff;
}

.upcoming a[href]:hover {
  border-color: #fff;
}

.released {
  display: grid;
  flex-wrap: wrap;
  padding: 2rem;
  grid-gap: 2rem;
  grid-template-columns: repeat(auto-fill, minmax(46rem, 1fr));
}

.released > * {
  display: flex;
  flex-direction: column;
  padding-bottom: 0.5rem;
}

.released figure {
  padding-bottom: 66%;
}

.released .title-row {
  border-bottom: 1px dashed currentColor;
  flex: 1;
}

/*.released a[href]:hover figure {
  border-color: #fff;
}*/

.sticker {
  position: absolute;
  top: 2rem;
  right: 10rem;
  width: 250px;
  z-index: 9999;
  overflow: hidden;
  user-select: none;
  /*mix-blend-mode: multiply;*/
}

.sticker > * {
  display: block;
  width: 98%;
  height: 98%;
  animation: swing 30s -15s ease-in-out alternate infinite;
}

.sticker img {
  width: 100%;
  height: 100%;
}

.sticker--desktop {
  display: block;
}
.sticker--mobile {
  display: none;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes swing {
  from {
    transform: rotate(-45deg);
  }
  to {
    transform: rotate(45deg);
  }
}

@media (max-width: 768px) {
  .sticker {
    width: 40vw;
    top: 4rem;
    right: 3rem;
  }
}

@media (max-width: 1000px) {
  section.productions {
    display: flex;
    flex-direction: column;
  }

  section.productions > hr:first-child {
    order: 2;
  }

  .upcoming figure {
    width: 26rem;
    height: 17rem;
  }
  .upcoming h2 {
    height: 17rem;
  }

  .released {
    display: block;
  }
  .released > * {
    margin-bottom: 2rem;
  }

  .sticker--desktop {
    display: none;
  }
  .sticker--mobile {
    display: block;
  }
}

@media (max-width: 1150px) {
  .released .title-row {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
